<template>
    <inline-tab-panel name="pages">
        <flex-container direction="column">
            <flex-container
                v-for="page in props.pages"
                :key="page.id.value"
                class="page-entry"
                justify-content="space-between"
            >
                <text-decorator :size="FontSize.sm">
                    {{ page.title }}
                </text-decorator>

                <button-plain-icon
                    :icon="IconPenToSquare"
                    :size="IconSize.sm"
                    is-custom-icon
                    @click="() => openPageEditor(page)"
                />
            </flex-container>
        </flex-container>
    </inline-tab-panel>

    <fullscreen-dialog
        ref="pageEditorDialog"
        label="Pagina bewerken"
        description="Pagina bewerken"
        without-padding
        hide-close
        :size="7"
    >
        <template #default>
            <page-editor
                v-if="pageEditorDialog?.isOpen && editingPage"
                :page="editingPage as Page"
                @back="closePageEditor"
                @saved="onPageSaved"
            />
        </template>
    </fullscreen-dialog>
</template>

<script setup lang="ts">
    import {
        ButtonPlainIcon,
        FlexContainer,
        FontSize,
        FullscreenDialog,
        IconSize,
        InlineTabPanel,
        TextDecorator,
        type ExposedOverlay,
    } from '@keyvue/one-ui';

    import { ref, type Ref } from 'vue';
    import type { Page, ChangePageCommand } from '@keyvue/one-api-client';
    import IconPenToSquare from '@keyvue/one-awesome-icons/icons/regular/pen-to-square.svg?component';
    import PageEditor from '@/flow/flow/components/page-editor/PageEditor.vue';

    interface Props {
        pages: Page[]
    }

    const props = defineProps<Props>();
    const emit = defineEmits<{
        (e: 'update:pages', pages: Page[]): void;
    }>();

    const pages = ref<Page[]>(props.pages) as Ref<Page[]>;

    const pageEditorDialog = ref<ExposedOverlay>();
    const editingPage = ref<Page | null>(null);

    function openPageEditor(page: Page) {
        editingPage.value = page;
        pageEditorDialog.value?.openOverlay();
    }

    function closePageEditor() {
        editingPage.value = null;
        pageEditorDialog.value?.closeOverlay();
    }

    function onPageSaved(command: ChangePageCommand, close: boolean = true) {
        pages.value.forEach((page: Page) => {
            if (page.id.value !== command.id) {
                return;
            }

            page.title = command.title; // eslint-disable-line no-param-reassign
            page.slug = command.slug; // eslint-disable-line no-param-reassign
            page.content = command.content; // eslint-disable-line no-param-reassign
            page.mainCss = command.mainCss; // eslint-disable-line no-param-reassign
            page.sectionCss = command.sectionCss; // eslint-disable-line no-param-reassign
        });

        emit('update:pages', pages.value);

        if (close) {
            closePageEditor();
        }
    }
</script>

<style scoped lang="scss">
.page-entry {
    border-bottom: solid 1px color(gray-300);
    padding-bottom: size(md);
}
</style>
